import {
  Alert,
  Badge,
  Button,
  type Column,
  DataTable,
  EmptyState,
  LinkButton,
  PageHeader,
  relativeTimeText,
  SearchField,
  Select,
  Skeleton,
  usePaged,
} from "@emdur/ui";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { ManifestRegistration } from "../components/ManifestRegistration";
import { RiskStrip } from "../components/Permissions";
import { useCatalogue } from "../components/useCatalogue";
import { useApi } from "../lib/apiContext";
import { type Client, appHref, clientsPath } from "../lib/clients";
import { useRegistry } from "../components/RegistryProvider";
import { appPermissionSummary } from "../lib/permissionCopy";

export const APP_KIND = {
  web: "Web app",
  native: "Native app",
  service: "Service",
};
export function AppKindIcon({ kind }: { kind: Client["kind"] }) {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="size-5 shrink-0"
    >
      <use href={`/brand/app-${kind}.svg#app-kind`} />
    </svg>
  );
}
export function Apps() {
  const { id = "" } = useParams();
  const api = useApi();
  const catalogue = useCatalogue(id);
  const registry = useRegistry();
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [kind, setKind] = useState("");
  const [params, setParams] = useSearchParams();
  const [registration, setRegistration] = useState(params.has("register"));
  const filters = new URLSearchParams();
  if (state) filters.set("state", state);
  if (kind) filters.set("kind", kind);
  const shared = registry.organisations.some(
    (org) => org.organization_id === id,
  );
  const paged = usePaged<Client>(
    !registry.loading && !shared
      ? `${clientsPath(id)}${filters.size ? `?${filters}` : ""}`
      : null,
    api.fetch,
  );
  const list =
    shared || registry.loading
      ? {
          items: registry.loading
            ? null
            : registry.apps.filter(
                (client) =>
                  client.organization_id === id &&
                  (!state || client.state === state) &&
                  (!kind || client.kind === kind),
              ),
          error: registry.error,
          more: null,
          loading: registry.loading,
          reload: registry.reload,
        }
      : paged;
  const rows = (list.items ?? []).filter((client) =>
    `${client.client_name} ${client.client_id}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const columns: Column<Client>[] = [
    {
      id: "name",
      label: "App",
      isRowHeader: true,
      sortValue: (client) => client.client_name,
      cell: (client) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="sr-only">{APP_KIND[client.kind]}</span>
          <LinkButton
            variant="quiet"
            icon={<AppKindIcon kind={client.kind} />}
            href={appHref(id, client.id)}
          >
            <span className="whitespace-normal wrap-anywhere">
              {client.client_name}
            </span>
          </LinkButton>
          <p
            title={appPermissionSummary(
              client.capabilities,
              catalogue.items ?? [],
            )}
            className="app-summary text-sm font-regular text-muted"
          >
            {appPermissionSummary(client.capabilities, catalogue.items ?? [])}
          </p>
        </div>
      ),
    },
    {
      id: "state",
      label: "State",
      sortValue: (client) => client.state,
      cell: (client) => (
        <Badge tone={client.state === "active" ? "positive" : "neutral"}>
          {client.state === "active" ? "Active" : "Revoked"}
        </Badge>
      ),
    },
    {
      id: "permissions",
      label: "Permissions",
      sortValue: (client) => client.capabilities.length,
      cell: (client) => (
        <span className="inline-flex flex-col gap-1">
          <span>
            {client.capabilities.length}{" "}
            {client.capabilities.length === 1 ? "permission" : "permissions"}
          </span>
          <RiskStrip
            permissions={client.capabilities}
            catalogue={catalogue.items ?? []}
          />
        </span>
      ),
    },
    {
      id: "used",
      label: "Last used",
      sortValue: (client) =>
        client.last_used_at ? Date.parse(client.last_used_at) : undefined,
      cell: (client) => {
        if (!client.last_used_at) return "Never used";
        const absolute = new Date(client.last_used_at).toLocaleString("en", {
          dateStyle: "full",
          timeStyle: "short",
        });
        return (
          <time
            dateTime={client.last_used_at}
            title={absolute}
            aria-label={absolute}
          >
            {relativeTimeText(client.last_used_at, "en")}
          </time>
        );
      },
    },
  ];
  const open = () => setRegistration(true);
  return (
    <div className="app-screen flex flex-col gap-4">
      <PageHeader
        title="Apps"
        description="Understand what your apps can do. Decide what they can access."
        actions={
          !!list.items?.length && (
            <Button variant="primary" onPress={open}>
              Register from a manifest
            </Button>
          )
        }
      />
      {(!!list.items?.length || !!search || !!state || !!kind) && (
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <div>
            <SearchField
              label="Search apps"
              value={search}
              onChange={setSearch}
            />
            <p className="mt-1 text-sm text-muted">
              Press <kbd>⌘ / Ctrl K</kbd> to go anywhere
            </p>
          </div>
          <Select
            label="State"
            value={state}
            onChange={setState}
            options={[
              { value: "", label: "All states" },
              { value: "active", label: "Active" },
              { value: "revoked", label: "Revoked" },
            ]}
          />
          <Select
            label="Kind"
            value={kind}
            onChange={setKind}
            options={[
              { value: "", label: "All kinds" },
              ...Object.entries(APP_KIND).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
          />
        </div>
      )}
      {(list.error || catalogue.error) && (
        <Alert tone="danger">
          {list.error || catalogue.error}
          <div className="mt-2">
            <Button onPress={() => void list.reload()}>Try again</Button>
          </div>
        </Alert>
      )}
      <div className="apps-table">
        {list.items === null ? (
          <div className="p-5">
            <Skeleton label="Loading apps" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={
              search || state || kind
                ? "No matching apps"
                : "Your apps start here"
            }
            action={
              <Button variant="primary" onPress={open}>
                Register from a manifest
              </Button>
            }
          >
            {search || state || kind ? (
              "Try another search or clear your filters."
            ) : (
              <span className="flex flex-col gap-4 text-start">
                <span>
                  A manifest names your app and the access it needs. Start with
                  a service that reads your organisation:
                </span>
                <code className="block whitespace-pre-wrap wrap-anywhere rounded-control bg-hover p-4 text-sm text-ink">
                  {JSON.stringify(
                    {
                      client_name: "Team directory",
                      grant_types: ["client_credentials"],
                      scope: "identity.organizations:read",
                    },
                    null,
                    2,
                  )}
                </code>
                <span>
                  The console creates its service key in your browser and
                  downloads it once.
                </span>
              </span>
            )}
          </EmptyState>
        ) : (
          <DataTable
            caption="Apps"
            columns={columns}
            rows={rows}
            rowKey={(client) => client.id}
            defaultSort={{ column: "name", direction: "ascending" }}
          />
        )}
      </div>
      {list.more && (
        <Button isLoading={list.loading} onPress={() => void list.more?.()}>
          Show more apps
        </Button>
      )}
      {registration && (
        <ManifestRegistration
          org={id}
          catalogue={catalogue.items ?? []}
          onClose={() => {
            setRegistration(false);
            if (params.has("register")) {
              params.delete("register");
              setParams(params, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}
